import React, { useMemo } from "react";
import CardLayout from "components/foundation/templates/CardLayout";
import SelectButton from "components/foundation/SelectButton";
import { CheckIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import styled from "styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { workFormStepState } from "store/document/workDocument";
import {
  NoneOrderDocumentClassDto,
  NoneOrderTagDetailResponse,
} from "apis/data-contracts";
import { DOCUMENT_UUID, DOCUMENT_CLASS_UUID } from "config/constant";
import Modal from "components/foundation/Modal/Modal";
import TempDocumentModal from "components/document/Create/TempDocumentModal";
import useCompaniesQuery from "features/tag/hooks/queries/useCompaniesQuery";
import { useGetDocumentTagsQuery } from "features/document/hooks/queries/useGetDocumentTagsQuery";
import useMediaView from "features/common/hooks/useMediaView";
import useWorkSelectFormStates from "features/document/hooks/create/useWorkSelectFormStates";
import { isFormReselectedState } from "store/document/documentCommon";
import useTempDocModal from "features/document/hooks/create/useTempDocModal";

const WorkCreateSelectPage = () => {
  const isSmallView = useMediaView("small");

  /* Recoil States */
  const {
    selectedCompany,
    onSelectCompany,

    selectedPj,
    onSelectPj,

    selectedDocumentClass,
    onSelectDocumentClass,

    selectedTopics,
    onSelectTopic,
  } = useWorkSelectFormStates();

  /* 옵션 목록 */
  const { companyOptions, pjOptions, documentClassOptions, topicOptions } =
    useWorkSelectFormOptions(isSmallView);

  /* 다음 버튼 */
  const { onClickNext } = useNextButton();

  /* 임시 저장 문서 모달 */
  const { openTempSaveDocsModal, setOpenTempSaveDocsModal } = useTempDocModal({
    categoryUuid: DOCUMENT_UUID.WORK.uuid,
  });

  const isFormReselected = useRecoilValue(isFormReselectedState);

  return (
    <>
      <CardLayout.Container
        header={{
          title: "문서 등록",
          description: "문서에 들어갈 기본 태그를 선택해 주세요.",
        }}
        gap={40}
      >
        <DocumentWorkCreateForm>
          <CardLayout.Field label="법인명">
            <SelectContainerStyle>
              {companyOptions.map((company) => {
                const selected = company.uuid === selectedCompany?.uuid;
                return (
                  <SelectButton
                    key={company.uuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => onSelectCompany(company)}
                  >
                    {company.name}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
          <CardLayout.Field label="PJ명">
            <SelectContainerStyle>
              {pjOptions.map((pj) => {
                const selected = pj.uuid === selectedPj?.tagUuid;
                return (
                  <SelectButton
                    key={pj.uuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => onSelectPj(pj)}
                  >
                    {pj.name}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
          <CardLayout.Field label="문서구분">
            <SelectContainerStyle>
              {documentClassOptions.map((documentClass) => {
                const selected =
                  documentClass.uuid === selectedDocumentClass?.uuid;

                return (
                  <SelectButton
                    key={documentClass.uuid}
                    type="button"
                    disabled={isFormReselected}
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => onSelectDocumentClass(documentClass)}
                  >
                    {documentClass.name}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
          <CardLayout.Field label="주제 분류">
            <SelectContainerStyle>
              {topicOptions.map((topic) => {
                const selected = !!selectedTopics.find(
                  (item) => item.tagUuid === topic.tagUuid,
                );
                return (
                  <SelectButton
                    key={topic.tagUuid}
                    type="button"
                    value={topic.tagName}
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => onSelectTopic(topic)}
                  >
                    {topic.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
        </DocumentWorkCreateForm>

        <Divider className="pc-only" />

        <Button
          variant="primary"
          size="large"
          disabled={!selectedCompany || !selectedDocumentClass}
          onClick={onClickNext}
        >
          다음
        </Button>
      </CardLayout.Container>

      <Modal.Root
        open={!isSmallView && openTempSaveDocsModal}
        onOpenChange={setOpenTempSaveDocsModal}
      >
        <TempDocumentModal
          defaultTabIndex={0}
          onCancel={() => setOpenTempSaveDocsModal(false)}
        />
      </Modal.Root>
    </>
  );
};

export default WorkCreateSelectPage;

/* Hooks */

const useWorkSelectFormOptions = (isSmallView: boolean) => {
  // 옵션 목록 : 법인명
  const { data: companyTagsData } = useCompaniesQuery();
  const companyOptions = companyTagsData.data.result;

  const { data: workTagResponse } = useGetDocumentTagsQuery({
    categoryUuid: DOCUMENT_UUID.WORK.uuid,
  });

  // 옵션 목록 : PJ명
  const pjOptions =
    workTagResponse?.find((category) => category.name === "PJ")?.tagList || [];

  // 옵션 목록 : 문서구분
  const documentClassOptions: NoneOrderDocumentClassDto[] = useMemo(() => {
    const documentClassUuidList = (
      isSmallView
        ? [DOCUMENT_UUID.WORK.classes.휴가신청서.uuid]
        : Object.keys(DOCUMENT_CLASS_UUID)
    ) as (keyof typeof DOCUMENT_CLASS_UUID)[];

    return documentClassUuidList.map((documentClassUuid) => ({
      uuid: documentClassUuid as string,
      name: DOCUMENT_CLASS_UUID[documentClassUuid] as string,
    }));
  }, [isSmallView]);

  // 태그 : 주제분류
  const topicOptions: NoneOrderTagDetailResponse[] =
    workTagResponse
      ?.find((category) => category.name === "주제분류")
      ?.tagList.map((tag) => ({
        tagUuid: tag.uuid,
        tagName: tag.name,
      })) || [];

  return {
    documentClassOptions,
    companyOptions,
    pjOptions,
    topicOptions,
  };
};

const useNextButton = () => {
  const setWorkFormStep = useSetRecoilState(workFormStepState);

  /**
   * @description 다음 버튼 클릭 이벤트 리스너
   */
  const onClickNext = () => {
    setWorkFormStep(1);
  };

  return {
    onClickNext,
  };
};

/* Styles */
const DocumentWorkCreateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5rem;

  ${({ theme }) => theme.media.small} {
    gap: 2.8rem;
  }
`;

const SelectContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.2rem, 1fr));
  gap: 1rem;
`;
